import React, { useEffect, useState, useRef } from 'react';
import * as yup from "yup";
import CustomTable from '../../components/common/Table.tsx';
import Searchbar from '../../components/common/Searchbar.tsx';
import SideSheet from '../../components/common/Sidesheet.tsx';
import { Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store.ts';
import Confirmation from '../../components/common/Confirmation.tsx';
import { notification, Pagination } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { IVendorReq, IVendorRes } from '../../type/type.ts';
import VendorForm from '../../components/sheet/VendorForm.tsx';
import { capitalizeFirstLetter } from '../../utils/util.service.ts';
import { setBreadcrumbs } from '../../slices/BreadcrumbSlice.ts';
import { 
  createVendor, 
  getAllVendors, 
  updateVendor, 
  deleteVendor, 
  setOffset, 
  setLimit, 
  setSortField, 
  setSortOrder,
  fetchVendor 
} from '../../slices/VendorSlice.ts';


const Vendors: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [isSideSheetOpen, setSideSheetOpen] = useState(false);
  const { vendors, loading, dataCount, offset, limit, getVendorByIdValue } = useSelector((state: RootState) => state.vendorSlice);
  const [selectedVendor, setSelectedVendor] = useState<number | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<IVendorRes | null>(null);

  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<IVendorReq | null>(
    null
  );
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    order: "ascend" | "descend" | null;
  }>({
    key: "",
    order: null
  });



    const initialValues: IVendorReq = {
        name: "",
        email: "",
        mobile: "",
        age: 0,
        status: "",
        TFN_number: "",
        license: "",
        proof_of_age: "",
        passport: "",
        city: "",
        state: "",
        country: "",
        address: "",
        pincode: "",
        latitude: "",
        longitude: "",
        service_id: 0,
        sub_service_id: 0
  };

  const showNotification = (type: "success" | "error", message: string) => {
    notification[type]({
      message,
      placement: "top",
    });
  };
  const handleSearch = (value: string) => {
    setQuery(value);
    dispatch(setOffset(0));
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {

    dispatch(setOffset(page - 1));
    if (newPageSize && newPageSize !== limit) {
      dispatch(setLimit(newPageSize));
    }
  };

  
  const handleSortChange = (_: any, __: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      sorter = sorter[0];
    }

    const { field, order } = sorter || {};
    const sortOrder = order === 'ascend' ? 1 : -1;

    setSortConfig({
      key: field || "",
      order: order || null
    });

    dispatch(setSortField(field));
    dispatch(setSortOrder(sortOrder));
    dispatch(setOffset(offset));
  };

  const handleEditClick = async (vendor: IVendorRes) => {
    try {
      setSelectedVendor(vendor.id);
      setSideSheetOpen(true);
      const response = await dispatch(fetchVendor({ vendorId: vendor.id })).unwrap();

      if (formikRef.current && response.data) {
        const vendorData = response.data;
        formikRef.current.setValues({
          name: vendorData.name || '',
          email: vendorData.email || '',
          mobile: vendorData.mobile || '',
          age: vendorData.age || 0,
          status: vendorData.status || '',
          TFN_number: vendorData.TFN_number || '',
          license: vendorData.license || '',
          proof_of_age: vendorData.proof_of_age || '',
          passport: vendorData.passport || '',
          city: vendorData.city || '',
          state: vendorData.state || '',
          country: vendorData.country || '',
          address: vendorData.address || '',
          pincode: vendorData.pincode || '',
          latitude: vendorData.latitude || '',
          longitude: vendorData.longitude || '',
          service_id: vendorData.service_id || 0,
          sub_service_id: vendorData.sub_service_id || 0
        });
      }
    } catch (error: any) {
      showNotification("error", "Failed to fetch vendor data");
      setSideSheetOpen(false);
      setSelectedVendor(null);
    }
  };

  const handleDeleteClick = (vendor: IVendorRes) => {
    setVendorToDelete(vendor);
    setConfirmationMessage("Are you sure you want to delete this vendor?");
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!vendorToDelete) return;

    try {
      await dispatch(deleteVendor(vendorToDelete.id)).unwrap();
      showNotification("success", "Vendor deleted successfully");
      dispatch(getAllVendors({
        searchInput: query,
        offset: offset,
        limit: limit,
        sortField: sortConfig.key || "created_at",
        sortOrder: sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
      }));
    } catch (error: any) {
      showNotification("error", error.message || "Failed to delete vendor");
    } finally {
      setIsDeleteConfirmationOpen(false);
      setVendorToDelete(null);
    }
  };

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        await dispatch(getAllVendors({
          offset: offset,
          limit: limit,
          sortField: sortConfig.key ?? null,
          sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
          searchInput: query
        })).unwrap();
      } catch (error) {
        console.error('Failed to fetch vendors:', error);
      }
    };
    
    fetchVendors();
  }, [dispatch, offset, limit, sortConfig, query]);


  useEffect(() => {
    dispatch(setBreadcrumbs([
      {
        title: "Vendor Management",
        path: '/vendors'
      }
    ]));
  }, [dispatch]);

  useEffect(() => {
    if (getVendorByIdValue?.data && selectedVendor && formikRef.current) {
      const vendorData = getVendorByIdValue.data;
      const currentValues = formikRef.current.values;
      const newValues = {
        name: vendorData.name || '',
        email: vendorData.email || '',
        mobile: vendorData.mobile || '',
        age: vendorData.age || 0,
        status: vendorData.status || '',
        TFN_number: vendorData.TFN_number || '',
        license: vendorData.license || '',
        proof_of_age: vendorData.proof_of_age || '',
        passport: vendorData.passport || '',
        city: vendorData.city || '',
        state: vendorData.state || '',
        country: vendorData.country || '',
        address: vendorData.address || '',
        pincode: vendorData.pincode || '',
        latitude: vendorData.latitude || '',
        longitude: vendorData.longitude || '',
        service_id: vendorData.service_id || 0,
        sub_service_id: vendorData.sub_service_id || 0
      };

      const hasChanges = Object.keys(newValues).some(key =>
        newValues[key as keyof typeof newValues] !== currentValues[key as keyof typeof currentValues]
      );

      if (hasChanges) {
        formikRef.current.setValues(newValues);
      }
    }
  }, [selectedVendor, getVendorByIdValue]);

  const columns = [
    {
      title: <span className="text-[#6D6D6D]">S.No</span>,
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) =>
        (offset * limit + index + 1).toString().padStart(2, "0"),
      width: 80,
    },
    {
      title: <span className="text-[#6D6D6D]">Name</span>,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "name" ? sortConfig.order : null,
      render: (name: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(name)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Phone</span>,
      dataIndex: 'mobile',
      key: 'mobile',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "mobile" ? sortConfig.order : null,
      render: (mobile: string | null) =>
        mobile ? <span className="text-[#0A0A0A] font-medium text-sm">{mobile}</span> : '-'
    },
    {
      title: <span className="text-[#6D6D6D]">Email</span>,
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "email" ? sortConfig.order : null,
      render: (email: string | null) =>
        email ? <span className="text-[#0A0A0A] font-medium text-sm">{email}</span> : '-'
    },
    {
      title: <span className="text-[#6D6D6D]">Age</span>,
      dataIndex: 'age',
      key: 'age',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "age" ? sortConfig.order : null,
      render: (age: number | null) =>
        age ? <span className="text-[#0A0A0A] font-medium text-sm">{age}</span> : '-'
    },
    {
      title: <span className="text-[#6D6D6D]">Status</span>,
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "status" ? sortConfig.order : null,
      render: (status: string | null) =>
        status ? <span className="text-[#0A0A0A] font-medium text-sm capitalize">{status}</span> : '-'
    },
    {
      title: <span className="text-[#6D6D6D]">Action</span>,
      dataIndex: 'action',
      key: 'action',
      render: (_: any, record: IVendorRes) => (
        <div className="flex items-center gap-3">
          <img
            src="/common/edit.svg"
            alt="Edit"
            aria-label="Edit Vendor"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleEditClick(record)}
          />
          <img
            src="/common/delete.svg"
            alt="Delete"
            aria-label="Delete Vendor"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      )
    }
  ];

  const vendorSchema = yup.object().shape({
    name: yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters"),
    email: yup.string()
      .required("Email is required")
      .email("Invalid email format"),
    mobile: yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
    age: yup.number()
      .required("Age is required")
      .min(18, "Age must be at least 18")
      .max(100, "Age must be less than 100"),
    status: yup.string()
      .required("Status is required"),
    TFN_number: yup.string()
      .required("TFN number is required"),
    license: yup.string()
      .required("License number is required"),
    proof_of_age: yup.string()
      .required("Proof of age is required"),
    passport: yup.string()
      .required("Passport number is required"),
    city: yup.string()
      .required("City is required"),
    state: yup.string()
      .required("State is required"),
    country: yup.string()
      .required("Country is required"),
    address: yup.string()
      .required("Address is required"),
    pincode: yup.string()
      .required("Pincode is required")
      .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
    latitude: yup.string()
      .required("Latitude is required"),
    longitude: yup.string()
      .required("Longitude is required"),
    service_id: yup.number()
      .required("Service is required"),
    sub_service_id: yup.number()
      .required("Sub-service is required")
  });

  const formikRef = useRef<FormikProps<IVendorReq>>(null);

  const onSubmit = async (values: IVendorReq, { resetForm }: any) => {
    const data: IVendorReq = {
      name: values.name,
      email: values.email,
      mobile: values.mobile,
      age: values.age,
      status: values.status,
      TFN_number: values.TFN_number,
      license: values.license,
      proof_of_age: values.proof_of_age,
      passport: values.passport,
      city: values.city,
      state: values.state,
      country: values.country,
      address: values.address,
      pincode: values.pincode,
      latitude: values.latitude,
      longitude: values.longitude,
      service_id: values.service_id,
      sub_service_id: values.sub_service_id
    };

    setConfirmationMessage(selectedVendor ? "Do you want to update the vendor?" : "Do you want to create a new vendor?");
    setIsConfirmationOpen(true);
    setConfirmAction(() => async () => {
      try {
        setSideSheetOpen(false);

        if (selectedVendor) {
          await dispatch(updateVendor({
            ...data,
            id: selectedVendor
          })).unwrap();
          showNotification("success", "Vendor updated successfully");
        } else {
          await dispatch(createVendor(data)).unwrap();
          showNotification("success", "Vendor created successfully");
        }

        // Refresh the vendor list after update/create
        await dispatch(getAllVendors({
          offset: offset,
          limit: limit,
          sortField: sortConfig.key ?? null,
          sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
          searchInput: query
        }));

        resetForm();
        setSideSheetOpen(false);
        setSelectedVendor(null);
      } catch (error: any) {
        showNotification("error", error.message || "Operation failed");
      }
      setIsConfirmationOpen(false);
    });
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={vendorSchema}
      enableReinitialize={true}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, values, touched, errors, setFieldValue, handleChange, resetForm }) => (
        <>
          <div className="bg-[#EDF1F6] h-[76px] mt-[10px] flex justify-between items-center px-[20px]">
            <p className="font-Montserrat font-semibold text-[20px] leading-[24.38px] text-[#0A0A0A]">Vendor Management</p>
            <div className="flex justify-end gap-3">
              <div className="flex justify-end">
                <Searchbar search={query} onSearch={handleSearch} />
              </div>
              <SideSheet
                btnLabel={selectedVendor ? "Edit Vendor" : "Add New Vendor"}
                width={800}
                title={selectedVendor ? "Edit Vendor" : "Add New Vendor"}
                isOpen={isSideSheetOpen}
                setOpen={setSideSheetOpen}
                onCancel={() => {
                  setSideSheetOpen(false);
                  resetForm();
                  setSelectedVendor(null);
                }}
                onSubmit={handleSubmit}
                submitLabel={selectedVendor ? "Update" : "Submit"}
              >
                <VendorForm
                  handleChange={handleChange}
                  values={values}
                  touched={touched}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />
              </SideSheet>
            </div>
          </div>

          <div>
            <CustomTable
              columns={columns}
              dataSource={vendors}
              loading={loading}
              onChange={handleSortChange}
              scrollHeight={800}
            />
          </div>

          <div className="sticky bottom-0 flex justify-end py-4">
            <Pagination
              total={dataCount}
              showSizeChanger={false}
              onChange={handlePaginationChange}
            />
          </div>

          <Confirmation
            label="Confirmation"
            message={confirmationMessage}
            isOpen={isConfirmationOpen || isDeleteConfirmationOpen}
            onClose={() => {
              setIsConfirmationOpen(false);
              setIsDeleteConfirmationOpen(false);
              setVendorToDelete(null);
            }}
            onConfirm={isDeleteConfirmationOpen ? handleDeleteConfirm : (confirmAction || (() => { }))}
            confirmButtonLabel="Confirm"
          />
        </>
      )}
    </Formik>
  );
};

export default Vendors;